import React, { useState, useEffect, useMemo, useRef } from "react";
import "./ShopDetails.css";

import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../../../Features/Cart/cartSlice";
import { addToWishList, removeFromWishList, wishlistItemId } from "../../../Features/Wishlist/wishListSlice";

import Filter from "../Filters/Filter";
import ContextHeroSlider from "../../Hero/ContextHeroSlider";
import { Link, useSearchParams } from "react-router-dom";
import { FiHeart } from "react-icons/fi";
import { FaStar } from "react-icons/fa";
import { IoFilterSharp, IoClose, IoChevronDown, IoGridOutline, IoAppsOutline, IoListOutline, IoSquareOutline } from "react-icons/io5";
import { FaCartPlus, FaSortAmountDown, FaSortAmountUp, FaFire, FaRegCalendarAlt } from "react-icons/fa";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import toast from "react-hot-toast";
import { getImageUrl, joinPublicStorefrontUrl } from "../../../api/client";
import Seo from "../../Seo/Seo";
import { useSiteSeo } from "../../../context/SiteSeoContext";
import { absoluteCanonicalUrl } from "../../../utils/siteUrl";
import { truncateMetaDescription } from "../../../utils/seoText";
import { usePrerenderReady } from "../../../utils/prerenderReady";

const SHOP_PRODUCT_QUERY_KEYS = ["category", "categoryId", "gender", "color", "size", "minPrice", "maxPrice", "q"];

const SHOP_SORT_URL_VALUES = new Set(["new_arrivals", "most_rented", "price_asc", "price_desc"]);

function shopSortFromUrl(searchParamsObj) {
  const s = String(searchParamsObj.get("sort") || "").trim();
  return SHOP_SORT_URL_VALUES.has(s) ? s : "new_arrivals";
}

const LOAD_MORE_PAGE_SIZE = 50;

/** Align with storefront gender filter (?category=Mens|Womens). */
function normalizeShopCategoryForGenderKey(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  try {
    return decodeURIComponent(s).trim().toLowerCase().replace(/\s+/g, " ");
  } catch (_) {
    return s.trim().toLowerCase().replace(/\s+/g, " ");
  }
}

function isGenderOnlyShopCategory(categoryFilter) {
  const k = normalizeShopCategoryForGenderKey(categoryFilter);
  return k === "mens" || k === "men" || k === "womens" || k === "women";
}

/** Same buckets as backend appendGenderFilterSql (list endpoint includes `gender`). */
function genderBucketFromProduct(genderRaw) {
  const k = String(genderRaw || "").trim().toLowerCase();
  if (
    ["female", "f", "women", "womens", "woman", "ladies", "girl", "girls"].includes(k)
  )
    return "female";
  if (["male", "m", "men", "mens", "man", "gents", "boy", "boys"].includes(k)) return "male";
  return "other";
}

/**
 * Alternate male / female in the grid for balanced visibility.
 * Preserves order within each gender from `list` (so price / Aâ€“Z sort still applies per side).
 */
function interleaveShopProductsByGender(list) {
  const male = [];
  const female = [];
  const other = [];
  for (const p of list) {
    const b = genderBucketFromProduct(p.gender);
    if (b === "male") male.push(p);
    else if (b === "female") female.push(p);
    else other.push(p);
  }
  if (!male.length || !female.length) return list;
  const out = [];
  const maxLen = Math.max(male.length, female.length);
  for (let n = 0; n < maxLen; n++) {
    if (n < male.length) out.push(male[n]);
    if (n < female.length) out.push(female[n]);
  }
  out.push(...other);
  return out;
}

/** Mix menâ€™s and womenâ€™s unless the URL is already filtered to one gender (?gender= or legacy ?category=Mens|Womens). */
function shouldMixGendersOnShop(categoryFilter, genderParam) {
  const g = String(genderParam || "").trim().toLowerCase();
  if (g === "male" || g === "female") return false;
  return !isGenderOnlyShopCategory(categoryFilter);
}

function dateRangeHasBlocked(start, end, blockedDates) {
  if (!start || !end) return false;
  const d1 = new Date(`${start}T12:00:00`);
  const d2 = new Date(`${end}T12:00:00`);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime()) || d2 < d1) return false;
  const blocked = new Set(blockedDates || []);
  for (let t = d1.getTime(); t <= d2.getTime(); t += 86400000) {
    const d = new Date(t);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (blocked.has(key)) return true;
  }
  return false;
}

function availabilityStatusForDates(productId, pickupDate, returnDate, availabilityByProduct) {
  if (!pickupDate || !returnDate) return "Select rental date to check availability";
  if (!Object.prototype.hasOwnProperty.call(availabilityByProduct, productId)) return "Checking availability…";
  const blockedDates = availabilityByProduct[productId];
  if (blockedDates === null) return "Unable to check availability";
  return dateRangeHasBlocked(pickupDate, returnDate, blockedDates) ? "Not available" : "Available for your dates";
}


const ShopDetails = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const siteCtx = useSiteSeo();
  const categoryFilter = searchParams.get("category") || "";
  const genderQuery = searchParams.get("gender") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const wishlistIds = useMemo(
    () => new Set(wishlistItems.map(wishlistItemId).filter(Boolean)),
    [wishlistItems]
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [sortBy, setSortBy] = useState(() => shopSortFromUrl(searchParams));
  const [pickupDate, setPickupDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [availabilityByProduct, setAvailabilityByProduct] = useState({});
  const [gridCols, setGridCols] = useState(3);
  const [visibleCount, setVisibleCount] = useState(LOAD_MORE_PAGE_SIZE);
  /** First index of the batch revealed by the last "Load more" â€” drives enter animation. */
  const [loadMoreEnterFrom, setLoadMoreEnterFrom] = useState(null);
  const scrollAfterLoadMoreRef = useRef(null);

  const shopQueryKey = searchParams.toString();

  useEffect(() => {
    setLoading(true);
    const parsed = new URLSearchParams(shopQueryKey);
    const apiQs = new URLSearchParams();
    SHOP_PRODUCT_QUERY_KEYS.forEach((k) => {
      const v = parsed.get(k);
      if (v != null && v !== "") apiQs.set(k, v);
    });
    apiQs.set("sort", sortBy);
    const q = apiQs.toString();
    fetch(joinPublicStorefrontUrl(`/api/products${q ? `?${q}` : ""}`))
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [shopQueryKey, sortBy]);

  useEffect(() => {
    setSortBy(shopSortFromUrl(searchParams));
  }, [shopQueryKey, searchParams]);

  useEffect(() => {
    setVisibleCount(LOAD_MORE_PAGE_SIZE);
    setLoadMoreEnterFrom(null);
    scrollAfterLoadMoreRef.current = null;
  }, [shopQueryKey]);

  const displayedProducts = useMemo(() => {
    // API returns the requested allowlisted sort order.
    return products;
  }, [products, categoryFilter, genderQuery]);

  const visibleProducts = useMemo(
    () => displayedProducts.slice(0, visibleCount),
    [displayedProducts, visibleCount]
  );

  const canLoadMore = !loading && displayedProducts.length > visibleCount;

  useEffect(() => {
    if (!pickupDate || !returnDate || visibleProducts.length === 0) {
      setAvailabilityByProduct({});
      return;
    }
    const start = new Date(`${pickupDate}T12:00:00`);
    const end = new Date(`${returnDate}T12:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      setAvailabilityByProduct({});
      return;
    }
    let cancelled = false;
    Promise.all(
      visibleProducts.map((p) =>
        fetch(joinPublicStorefrontUrl(`/api/products/${p.id}/availability?from=${pickupDate}&to=${returnDate}`))
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
          .then((data) => [p.id, Array.isArray(data.blocked_dates) ? data.blocked_dates : []])
          .catch(() => [p.id, null])
      )
    ).then((entries) => {
      if (!cancelled) setAvailabilityByProduct(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [pickupDate, returnDate, visibleProducts]);

  useEffect(() => {
    const from = scrollAfterLoadMoreRef.current;
    if (from == null) return;
    scrollAfterLoadMoreRef.current = null;
    const id = displayedProducts[from]?.id;
    if (!id) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById(`shop-product-${id}`)?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      });
    });
  }, [visibleCount, displayedProducts]);

  useEffect(() => {
    if (loadMoreEnterFrom == null) return undefined;
    const t = window.setTimeout(() => setLoadMoreEnterFrom(null), 550);
    return () => window.clearTimeout(t);
  }, [loadMoreEnterFrom]);

  usePrerenderReady(!loading);

  const titleSuffix =
    siteCtx.organization_name && String(siteCtx.organization_name).trim()
      ? ` | ${String(siteCtx.organization_name).trim()}`
      : " | Gem Fashion Studios";

  const listTitle =
    categoryFilter.trim() ? `${decodeURIComponent(categoryFilter.trim())} rentals` : "Shop rentals";
  const listDescRaw = categoryFilter.trim()
    ? `Rent designer outfits in ${decodeURIComponent(categoryFilter.trim())}. Sizes, colours, and rental dates.`
    : "Browse designer clothing and outfits available to rent. Filter by occasion, gender, and size.";
  const metaTitle = `${listTitle}${titleSuffix}`;

  const handleLoadMore = () => {
    const from = visibleCount;
    const next = Math.min(visibleCount + LOAD_MORE_PAGE_SIZE, displayedProducts.length);
    if (next <= from) return;
    scrollAfterLoadMoreRef.current = from;
    setLoadMoreEnterFrom(from);
    setVisibleCount(next);
  };

  const handleWishlistClick = (product) => {
    const id = String(product.id);
    if (wishlistIds.has(id)) {
      dispatch(removeFromWishList(product));
      toast.success("Removed from wishlist");
      return;
    }
    dispatch(addToWishList({
      id: product.id,
      productId: product.id,
      productID: product.id,
      productName: product.name,
      productImg: getImageUrl(product.first_image) || "",
      productPrice: Number(product.min_price_paise || 0) / 100,
    }));
    toast.success("Added to wishlist");
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const cartItems = useSelector((state) => state.cart.items);

  const handleAddToCart = (p) => {
    const product = {
      productId: p.id,
      productID: p.id,
      productName: p.name,
      productPrice: p.min_price_paise ? p.min_price_paise / 100 : 0,
      frontImg: getImageUrl(p.first_image) || "",
      rent_days: 1,
      event_date: "",
      productReviews: "Rental",
    };
    const productInCart = cartItems.find(
      (item) => (item.productID ?? item.productId) === p.id && (item.event_date ?? "") === ""
    );
    if (productInCart && productInCart.quantity >= 20) {
      toast.error("Product limit reached", {
        duration: 2000,
        style: { backgroundColor: "#ff4b4b", color: "white" },
        iconTheme: { primary: "#fff", secondary: "#ff4b4b" },
      });
    } else {
      dispatch(addToCart(product));
      toast.success("Added! Set event date on product page.", {
        duration: 2000,
        style: { backgroundColor: "#07bc0c", color: "white" },
        iconTheme: { primary: "#fff", secondary: "#07bc0c" },
      });
    }
  };

  return (
    <>
      <Seo
        title={metaTitle}
        description={truncateMetaDescription(listDescRaw)}
        canonicalUrl={absoluteCanonicalUrl("/shop")}
      />
      <ContextHeroSlider context="shop" contextKey={categoryFilter} />
      <div className="shopDetails">
        <div className="shopDetailMain">
          <div className="shopDetails__left">
            <Filter />
          </div>
          <div className="shopDetails__right">
            <div className="shopDetailsBreadcrumbLink">
              <Link to="/" onClick={scrollToTop}>
                Home
              </Link>
              &nbsp;/&nbsp;
              <Link to="/shop">Browse Rentals</Link>
              {categoryFilter.trim() ? (
                <>
                  &nbsp;/&nbsp;
                  <span className="shopDetailsBreadcrumbCurrent">
                    {decodeURIComponent(categoryFilter.trim())}
                  </span>
                </>
              ) : null}
            </div>
            {categoryFilter.trim() ? (
              <h1 className="shopCollectionTitle">
                {decodeURIComponent(categoryFilter.trim())} collection
              </h1>
            ) : null}
            <div className="shopDetailsSorting">
              <div className="filterLeft" onClick={toggleDrawer}>
                <IoFilterSharp />
                <p>Filter</p>
              </div>
              <div className="shopDetailsSort">
                {/* Left: sort dropdown */}
                <div className="shopSortLeft">
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    size="small"
                    variant="outlined"
                    aria-label="Sort products"
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 500,
                      minWidth: 175,
                      bgcolor: '#fff',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: '#ddd' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#111' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#111' },
                    }}
                  >
                    <MenuItem value="new_arrivals"><span className="sortMenuRow"><FaRegCalendarAlt size={13} /> New Arrivals</span></MenuItem>
                    <MenuItem value="most_rented"><span className="sortMenuRow"><FaFire size={13} /> Most Rented</span></MenuItem>
                    <MenuItem value="price_asc"><span className="sortMenuRow"><FaSortAmountDown size={13} /> Price Low to High</span></MenuItem>
                    <MenuItem value="price_desc"><span className="sortMenuRow"><FaSortAmountUp size={13} /> Price High to Low</span></MenuItem>
                  </Select>
                </div>

                {/* Right: grid switcher */}
                <div className="shopGridSwitcher">
                  <Tooltip title="2 columns"><button type="button" className={`gridBtn${gridCols === 2 ? ' active' : ''}`} onClick={() => setGridCols(2)}><IoListOutline size={18} /></button></Tooltip>
                  <Tooltip title="3 columns"><button type="button" className={`gridBtn${gridCols === 3 ? ' active' : ''}`} onClick={() => setGridCols(3)}><IoGridOutline size={18} /></button></Tooltip>
                  <Tooltip title="4 columns"><button type="button" className={`gridBtn${gridCols === 4 ? ' active' : ''}`} onClick={() => setGridCols(4)}><IoAppsOutline size={18} /></button></Tooltip>
                  <Tooltip title="1 column"><button type="button" className={`gridBtn${gridCols === 1 ? ' active' : ''}`} onClick={() => setGridCols(1)}><IoSquareOutline size={18} /></button></Tooltip>
                </div>

                <div className="filterRight" onClick={toggleDrawer}>
                  <div className="filterSeprator"></div>
                  <IoFilterSharp />
                  <p>Filter</p>
                </div>
              </div>
            </div>
            <div className="shopAvailabilityRange" aria-label="Rental date range">
              <label>
                Pickup date
                <input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} />
              </label>
              <label>
                Return date
                <input type="date" min={pickupDate || undefined} value={returnDate} onChange={(e) => setReturnDate(e.target.value)} />
              </label>
            </div>
            <div className="shopDetailsProducts">
              <div className={`shopDetailsProductsContainer shopDetailsProductsContainer--cols-${gridCols}`}>
                {loading ? (
                  <p style={{ padding: "2rem", textAlign: "center" }}>Loading productsâ€¦</p>
                ) : displayedProducts.length === 0 ? (
                  <p style={{ padding: "2rem", textAlign: "center" }}>
                    {categoryFilter.trim()
                      ? `No products in this collection yet.`
                      : "No products yet. Check back later."}
                  </p>
                ) : (
                  visibleProducts.map((product, idx) => (
                    <div
                      className={`sdProductContainer${
                        loadMoreEnterFrom != null && idx >= loadMoreEnterFrom
                          ? " sdProductContainer--enter"
                          : ""
                      }`}
                      key={product.id}
                      id={`shop-product-${product.id}`}
                    >
                      <div className="sdProductImages">
                        <Link to={`/product/${product.id}`} onClick={scrollToTop}>
                          <img
                            src={getImageUrl(product.first_image) || ""}
                            alt=""
                            className="sdProduct_front"
                          />
                          <img
                            src={getImageUrl(product.first_image) || ""}
                            alt=""
                            className="sdProduct_back"
                          />
                        </Link>
                        <Link to={`/product/${product.id}`} onClick={scrollToTop} className="sdRentNowBtn">
                          Rent Now
                        </Link>
                      </div>
                      <div
                        className="sdProductImagesCart"
                        onClick={() => handleAddToCart(product)}
                      >
                        <FaCartPlus />
                      </div>
                      <div className="sdProductInfo">
                        <div className="sdProductCategoryWishlist">
                          <p>{product.category?.name || "Rental"}</p>
                          <FiHeart
                            onClick={() => handleWishlistClick(product)}
                            style={{
                              color: wishlistIds.has(String(product.id)) ? "red" : "#767676",
                              cursor: "pointer",
                            }}
                          />
                        </div>
                        <div className="sdProductNameInfo">
                          <Link to={`/product/${product.id}`} onClick={scrollToTop}>
                            <h5>{product.name}</h5>
                          </Link>
                          <p>
                            {product.min_price_paise != null
                              ? `From â‚¹${(product.min_price_paise / 100).toLocaleString("en-IN")}`
                              : "View options"}
                          </p>
                          <p className={`sdAvailabilityStatus ${availabilityStatusForDates(product.id, pickupDate, returnDate, availabilityByProduct).startsWith("Available") ? "available" : availabilityStatusForDates(product.id, pickupDate, returnDate, availabilityByProduct).startsWith("Not") ? "notAvailable" : "pending"}`}>
                            {availabilityStatusForDates(product.id, pickupDate, returnDate, availabilityByProduct)}
                          </p>
                          <div className="sdProductRatingReviews">
                            <div className="sdProductRatingStar">
                              <FaStar color="#FEC78A" size={10} />
                              <FaStar color="#FEC78A" size={10} />
                              <FaStar color="#FEC78A" size={10} />
                              <FaStar color="#FEC78A" size={10} />
                              <FaStar color="#FEC78A" size={10} />
                            </div>
                            <span>Rental</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            {displayedProducts.length > 0 && (
              <div className="shopDetailsLoadMore">
                {canLoadMore ? (
                  <button
                    type="button"
                    className="shopDetailsLoadMoreBtn"
                    onClick={handleLoadMore}
                  >
                    Load more
                    <IoChevronDown aria-hidden className="shopDetailsLoadMoreBtnIcon" />
                  </button>
                ) : visibleCount > LOAD_MORE_PAGE_SIZE ? (
                  <p className="shopDetailsLoadMoreEnd">
                    Showing all {displayedProducts.length} products
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Drawer */}
      <div className={`filterDrawer ${isDrawerOpen ? "open" : ""}`}>
        <div className="drawerHeader">
          <p>Filter By</p>
          <IoClose onClick={closeDrawer} className="closeButton" size={26} />
        </div>
        <div className="drawerContent">
          <Filter />
        </div>
      </div>
    </>
  );
};

export default ShopDetails;
